import React from 'react';
import { ClompApi } from '../api/ClompApi';

const api = new ClompApi();

const DEFAULT_DAYS = 30;

// "2026-10-15" as "Oct 15" (with the year when it isn't this one)
function dayName(day, withYear) {
    let [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
}

const number = (n) => (n || 0).toLocaleString();

// A headline number and what it counts
function Stat({ value, label, sub }) {
    return (
        <div className="adm-stat">
            <strong>{number(value)}</strong>
            <span>{label}</span>
            {sub ? <span className="adm-stat-sub">{sub}</span> : null}
        </div>
    );
}

// Visits a day as bars, oldest on the left, with the day's visits and unique visitors shown on hover or focus
function DailyChart({ daily }) {
    let [hover, setHover] = React.useState(null);
    let most = Math.max(1, ...daily.map((day) => day.visits));
    // a few gridline values, rounded to a tidy step
    let step = Math.pow(10, Math.floor(Math.log10(most)));
    if (most / step > 5) {
        step *= 2;
    }
    let top = Math.max(step, Math.ceil(most / step) * step);
    let lines = [];
    for (let value = 0; value <= top; value += step) {
        lines.push(value);
    }
    let shown = hover !== null ? daily[hover] : null;
    let thisYear = String(new Date().getFullYear());
    let labelEvery = Math.ceil(daily.length / 6);
    return (
        <figure className="adm-chart">
            <figcaption className="adm-chart-title">Visits a day</figcaption>
            <div className="adm-chart-plot" onMouseLeave={() => setHover(null)}>
                <div className="adm-chart-grid" aria-hidden="true">
                    {lines.map((value) => (
                        <div key={value} className="adm-chart-line" style={{ bottom: `${(value / top) * 100}%` }}><span>{number(value)}</span></div>
                    ))}
                </div>
                <div className="adm-chart-bars">
                    {daily.map((day, i) => (
                        <button key={day.day} type="button" className={`adm-chart-col${hover === i ? ' is-hover' : ''}`}
                            aria-label={`${dayName(day.day, true)}: ${number(day.visits)} visits, ${number(day.unique)} unique visitors`}
                            onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
                            <span className="adm-chart-bar" style={{ height: `${(day.visits / top) * 100}%` }} />
                        </button>
                    ))}
                </div>
                {shown ?
                    <div className="adm-chart-tip" role="status" style={{ left: `${((hover + 0.5) / daily.length) * 100}%` }}>
                        <strong>{dayName(shown.day, !shown.day.startsWith(thisYear))}</strong>
                        <span>{number(shown.visits)} visits</span>
                        <span>{number(shown.unique)} unique visitors</span>
                    </div>
                    : null}
            </div>
            <div className="adm-chart-axis" aria-hidden="true">
                {daily.map((day, i) => (
                    <span key={day.day} style={{ left: `${((i + 0.5) / daily.length) * 100}%` }}>
                        {i % labelEvery === 0 || i === daily.length - 1 ? dayName(day.day, false) : ''}
                    </span>
                )).filter((label, i) => i % labelEvery === 0 || i === daily.length - 1)}
            </div>
        </figure>
    );
}

// The admin panel's Analytics page, for Admins and Founders: how many pages have been opened and by how many
// different visitors, today, over a chosen span of days and since counting began, a day by day chart, and the most
// visited pages.
export class AdminAnalytics extends React.Component {

    state = {
        days: DEFAULT_DAYS,
        ranges: [7, 30, 90, 365],
        data: null,
        loading: true,
        failed: false,
        // the day by day numbers are shown as a table instead of the chart
        asTable: false
    };

    componentDidMount() {
        this.load(DEFAULT_DAYS);
    }

    load(days) {
        this.setState({ days, loading: true, failed: false });
        api.admin.getAnalytics(days).then((data) => {
            if (this.state.days === days) {
                this.setState({ loading: false, data, ranges: data.ranges || this.state.ranges });
            }
        }).catch((err) => {
            console.log('Error loading analytics: ', err);
            this.setState({ loading: false, failed: true });
        });
    }

    render() {
        let { days, ranges, data, loading, failed, asTable } = this.state;
        return (
            <section>
                <div className="adm-head">
                    <h2>Analytics</h2>
                    <div className="adm-tabs" role="group" aria-label="Span of days">
                        {ranges.map((range) => (
                            <button key={range} type="button" className={days === range ? 'is-on' : ''} aria-pressed={days === range} onClick={() => this.load(range)}>
                                {range === 365 ? '1 year' : `${range} days`}
                            </button>
                        ))}
                    </div>
                </div>
                <p className="adm-note">
                    A visit is a page being opened (opening the same page again within 10 seconds isn't counted twice). Unique visitors are told apart by a
                    random id their browser keeps, so one person on two devices counts twice. Days go by UTC.
                </p>
                {failed ? <p className="adm-error" role="alert">Couldn't load the analytics. Please try again later.</p>
                    : !data ? <p className="adm-empty">Loading analytics...</p>
                    :
                    <div className={loading ? 'adm-analytics is-loading' : 'adm-analytics'}>
                        <div className="adm-stats">
                            <Stat value={data.today.visits} label="Visits today" sub={`${number(data.today.unique)} unique visitors`} />
                            <Stat value={data.range.visits} label={`Visits, last ${days === 365 ? 'year' : `${days} days`}`} sub={`${number(data.range.unique)} unique visitors`} />
                            <Stat value={data.range.members} label="Logged-in players" sub={`last ${days === 365 ? 'year' : `${days} days`}`} />
                            <Stat value={data.allTime.visits} label="Visits, all time" sub={`${number(data.allTime.unique)} unique visitors`} />
                        </div>

                        <div className="adm-chart-head">
                            <button type="button" className="adm-details" onClick={() => this.setState({ asTable: !asTable })}>{asTable ? 'Show chart' : 'Show as table'}</button>
                        </div>
                        {asTable ?
                            <div className="adm-table-wrap adm-daily-table">
                                <table className="adm-table">
                                    <thead><tr><th>Day</th><th>Visits</th><th>Unique visitors</th></tr></thead>
                                    <tbody>
                                        {data.daily.slice().reverse().map((day) => (
                                            <tr key={day.day}><td>{dayName(day.day, true)}</td><td>{number(day.visits)}</td><td>{number(day.unique)}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            : <DailyChart daily={data.daily} />}

                        <h3 className="adm-sub">Most visited pages</h3>
                        {data.pages.length === 0 ? <p className="adm-empty">No visits in this span yet.</p> :
                            <div className="adm-table-wrap">
                                <table className="adm-table adm-pages">
                                    <thead><tr><th>Page</th><th>Visits</th><th>Unique visitors</th></tr></thead>
                                    <tbody>
                                        {data.pages.map((page) => (
                                            <tr key={page.path}><td><code>{page.path}</code></td><td>{number(page.visits)}</td><td>{number(page.unique)}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        }
                    </div>
                }
            </section>
        );
    }

}
